import { useMemo } from 'react';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface TimeRange {
  /** ISO instant, inclusive */
  from: string;
  /** ISO instant, exclusive */
  to: string;
}

interface PresetDef {
  id: string;
  label: string;
  /** Returns a range relative to "now"; called fresh on each click */
  build: () => TimeRange;
}

const SUMMIT_FROM = '2026-04-20T00:00:00Z';
/** First calendar month for which we have any data. Floor for month chips. */
const FIRST_DATA_MONTH = '2026-04';

function rollingWindow(days: number): TimeRange {
  const now = Date.now();
  return {
    from: new Date(now - days * DAY_MS).toISOString(),
    to: new Date(now).toISOString(),
  };
}

/** Build a TimeRange covering one calendar month (UTC), given 'YYYY-MM'. */
function monthWindow(yearMonth: string): TimeRange {
  const start = new Date(`${yearMonth}-01T00:00:00Z`);
  const next = new Date(start.getTime());
  next.setUTCMonth(next.getUTCMonth() + 1);
  return { from: start.toISOString(), to: next.toISOString() };
}

/** 'YYYY-MM' → 'MMM YYYY' (e.g. '2026-05' → 'May 2026'). */
function formatMonthLabel(yearMonth: string): string {
  const d = new Date(`${yearMonth}-01T00:00:00Z`);
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Generate per-month preset chips from FIRST_DATA_MONTH up to and including
 * the current calendar month. Returns oldest-first; the rendered list will
 * show them right after the rolling presets.
 */
function buildMonthPresets(): PresetDef[] {
  const now = new Date();
  const out: PresetDef[] = [];
  const cursor = new Date(`${FIRST_DATA_MONTH}-01T00:00:00Z`);
  const stop = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  while (cursor.getTime() < stop.getTime()) {
    const y = cursor.getUTCFullYear();
    const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    const ym = `${y}-${m}`;
    out.push({
      id: `month-${ym}`,
      label: formatMonthLabel(ym),
      build: () => monthWindow(ym),
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return out;
}

const ROLLING_PRESETS: PresetDef[] = [
  { id: '7d', label: '7d', build: () => rollingWindow(7) },
  { id: '14d', label: '14d', build: () => rollingWindow(14) },
  { id: '30d', label: '30d', build: () => rollingWindow(30) },
  { id: '90d', label: '90d', build: () => rollingWindow(90) },
  {
    id: 'summit',
    label: 'Since Summit',
    build: () => ({ from: SUMMIT_FROM, to: new Date().toISOString() }),
  },
];

interface Props {
  selectedPresetId: string;
  range: TimeRange;
  onChange: (range: TimeRange, presetId: string) => void;
}

export function TimeRangeSelector({ selectedPresetId, range, onChange }: Props): JSX.Element {
  const formatted = useMemo(() => {
    const fromDate = new Date(range.from).toISOString().slice(0, 10);
    const toDate = new Date(range.to).toISOString().slice(0, 10);
    return `${fromDate} → ${toDate}`;
  }, [range]);

  // Month presets grow each calendar month, so rebuild on every render. The
  // list is tiny (≤24 entries through 2027) so the cost is negligible.
  const monthPresets = buildMonthPresets();

  const renderPreset = (preset: PresetDef): JSX.Element => {
    const selected = preset.id === selectedPresetId;
    return (
      <button
        key={preset.id}
        type="button"
        role="tab"
        aria-selected={selected}
        className={selected ? 'time-range-chip time-range-chip-active' : 'time-range-chip'}
        onClick={() => onChange(preset.build(), preset.id)}
      >
        {preset.label}
      </button>
    );
  };

  return (
    <div className="time-range-selector">
      <div className="time-range-presets" role="tablist" aria-label="Time range">
        {ROLLING_PRESETS.map(renderPreset)}
        {monthPresets.length > 0 && (
          <span className="time-range-divider" aria-hidden="true">
            ·
          </span>
        )}
        {monthPresets.map(renderPreset)}
      </div>
      <span className="time-range-window">{formatted} (UTC)</span>
    </div>
  );
}

export function defaultTimeRange(): { presetId: string; range: TimeRange } {
  return { presetId: '7d', range: rollingWindow(7) };
}
